/**
 * Form Engine
 * Thin coordinator that orchestrates form state, validation, and event dispatch.
 * Delegates to FormStateManager (state) and FormEventEmitter (events) to satisfy SRP.
 * Accepts an optional IValidationEngine via constructor injection (DIP).
 */

import type {
  FormSchema,
  FormControl,
  FormValidationResult,
  IValidationEngine,
  ValidationError,
  FieldValidationResult,
} from '@atlas-forms/types-js';
import { ValidationEngine } from '@atlas-forms/validation-js';
import { FormEventEmitter } from './FormEventEmitter';
import { FormStateManager } from './FormStateManager';
import { BindingResolver } from './BindingResolver';
import { DataBindingEngine, type DataSource } from './DataBindingEngine';
import { ExpressionEvaluator } from './ExpressionEvaluator';

/**
 * Event types for form state changes
 */
export type FormEngineEventType = 'change' | 'blur' | 'focus' | 'submit' | 'validate' | 'reset' | 'error';

export interface FormEngineEvent {
  type: FormEngineEventType;
  fieldId?: string;
  value?: unknown;
  error?: string;
  timestamp: number;
}

/**
 * Returns the initial value for a control. A multiselect's default may be stored as a
 * comma-separated string (e.g. "updates,events"); it becomes one selection per item.
 * A string that exactly matches one option value stays a single selection, so option
 * labels containing commas are not split.
 */
function normalizeDefaultValue(control: FormControl): unknown {
  const raw = control.defaultValue;
  if (control.type !== 'multiselect' || typeof raw !== 'string') {
    return raw;
  }
  const trimmed = raw.trim();
  if (trimmed === '') {
    return [];
  }
  const options = (control.config?.options as Array<{ value: string }> | undefined) ?? [];
  if (options.some((o) => o.value === trimmed)) {
    return [trimmed];
  }
  return trimmed
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

/**
 * Form engine for state management and validation
 */
export class FormEngine {
  private schema: FormSchema;
  private state: FormStateManager;
  private emitter: FormEventEmitter;
  private validator: IValidationEngine;
  private controlIndex: Map<string, FormControl>;
  private bindingResolver: BindingResolver;
  /** Guards against recursive expression re-evaluation */
  private _applyingBindings = false;

  /**
   * @param schema - Form schema defining fields and validation rules
   * @param initialValues - Initial field values (optional)
   * @param validator - Validation engine instance (optional; defaults to ValidationEngine)
   * @param bindingEngine - DataBindingEngine instance (optional; created internally otherwise)
   */
  constructor(
    schema: FormSchema,
    initialValues?: Record<string, unknown>,
    validator?: IValidationEngine,
    bindingEngine?: DataBindingEngine
  ) {
    this.schema = schema;
    this.state = new FormStateManager(initialValues);
    this.emitter = new FormEventEmitter();
    this.validator = validator ?? new ValidationEngine();
    this.controlIndex = this.buildControlIndex();
    this.bindingResolver = new BindingResolver(bindingEngine);

    // Initialize all form control fields with default values
    this.initializeFields();
  }

  /**
   * Initialize form fields with default values
   */
  private initializeFields(): void {
    for (const control of this.getAllControls()) {
      if (!(control.id in this.state.values) && control.defaultValue !== undefined) {
        this.state.values[control.id] = normalizeDefaultValue(control);
      }
    }
  }

  /**
   * Build a Map<fieldId, FormControl> from the current schema.
   * Called once on construction and again whenever the schema changes.
   */
  private buildControlIndex(): Map<string, FormControl> {
    const index = new Map<string, FormControl>();

    if (this.schema.controls) {
      for (const control of this.schema.controls) {
        index.set(control.id, control);
      }
    }

    return index;
  }

  /**
   * Get all form controls as a flat list (O(n) — use sparingly).
   */
  private getAllControls(): FormControl[] {
    return Array.from(this.controlIndex.values());
  }

  /**
   * Get specific control by field ID — O(1) via index.
   */
  private getControl(fieldId: string): FormControl | undefined {
    return this.controlIndex.get(fieldId);
  }

  // ── State reads ─────────────────────────────────────────────────────────────

  getValues(): Record<string, unknown> {
    return { ...this.state.values };
  }

  getFieldValue(fieldId: string): unknown {
    return this.state.values[fieldId];
  }

  getErrors(): Record<string, string> {
    return { ...this.state.errors };
  }

  getFieldError(fieldId: string): string | undefined {
    return this.state.errors[fieldId];
  }

  isValid(): boolean {
    return Object.keys(this.state.errors).length === 0;
  }

  isDirty(): boolean {
    return this.state.dirty;
  }

  isFieldTouched(fieldId: string): boolean {
    return this.state.touched.has(fieldId);
  }

  get submitting(): boolean {
    return this.state.isSubmitting;
  }

  getSchema(): FormSchema {
    return this.schema;
  }

  // ── State mutations ──────────────────────────────────────────────────────────

  setFieldValue(fieldId: string, value: unknown): void {
    this.state.values[fieldId] = value;
    this.state.dirty = true;

    const control = this.getControl(fieldId);
    if (control) {
      const error = this.validator.validateField(control, value);
      if (error) {
        this.state.errors[fieldId] = error.message;
      } else {
        delete this.state.errors[fieldId];
      }
    }

    this.emitter.emit('change', { fieldId, value });

    // Synchronously re-evaluate expression bindings that reference this field
    this.reevaluateExpressionBindings(fieldId);
  }

  setValues(values: Record<string, any>): void {
    Object.entries(values).forEach(([fieldId, value]) => {
      this.state.values[fieldId] = value;
    });
    this.state.dirty = true;
    this.emitter.emit('change', { value: values });
  }

  // ── Binding API ──────────────────────────────────────────────────────────────

  /**
   * Resolves all `control.binding` entries in the schema and applies the results
   * to form state. Call this after construction to seed values from external data
   * sources, or after registering a new data source.
   *
   * @param externalData - Optional data to expose as `$json` / keyed sources
   */
  async applyBindings(externalData?: Record<string, any>): Promise<void> {
    if (this._applyingBindings) return;
    this._applyingBindings = true;

    try {
      if (externalData) {
        this.bindingResolver.setData(externalData);
      }

      this.bindingResolver.setFormValues(this.getValues());

      const results = await this.bindingResolver.resolveForm(
        this.schema,
        this.getValues(),
        { resolveDefaults: false, cache: false }
      );

      let changed = false;
      for (const result of results) {
        if (result.success && result.source !== 'form' && result.value !== undefined) {
          this.state.values[result.fieldId] = result.value;
          changed = true;
        }
      }

      if (changed) {
        this.emitter.emit('change', { value: this.getValues() });
      }
    } finally {
      this._applyingBindings = false;
    }
  }

  /**
   * Register an external data source with the binding engine.
   * After registering, call `applyBindings()` to re-resolve values.
   */
  registerDataSource(source: DataSource): void {
    this.bindingResolver.registerDataSource(source);
  }

  /**
   * Expose the underlying BindingResolver for advanced use (e.g. registering
   * custom evaluator functions or inspecting the dependency graph).
   */
  getBindingResolver(): BindingResolver {
    return this.bindingResolver;
  }

  /**
   * Synchronously re-evaluates expression bindings whose expressions reference
   * `changedFieldId`. Only expression-type bindings are handled here; source/path
   * bindings that require async resolution are left to `applyBindings()`.
   */
  private reevaluateExpressionBindings(changedFieldId: string): void {
    if (this._applyingBindings) return;

    const context = {
      values: this.getValues(),
      data: {},
      functions: {},
      variables: {},
    };

    let changed = false;
    for (const control of this.getAllControls()) {
      if (!control.binding?.expression) continue;
      if (control.id === changedFieldId) continue;

      // Only re-evaluate if expression references the changed field
      const expr = control.binding.expression;
      if (!expr.includes(changedFieldId)) continue;

      try {
        const newValue = ExpressionEvaluator.evaluate(expr, context);
        if (this.state.values[control.id] !== newValue) {
          this.state.values[control.id] = newValue;
          changed = true;
        }
      } catch {
        // Expression evaluation failure is non-fatal
      }
    }

    if (changed) {
      this.emitter.emit('change', { value: this.getValues() });
    }
  }

  touchField(fieldId: string): void {
    this.state.touched.add(fieldId);
    this.emitter.emit('blur', { fieldId });
  }

  focusField(fieldId: string): void {
    this.emitter.emit('focus', { fieldId });
  }

  setSubmitting(submitting: boolean): void {
    this.state.isSubmitting = submitting;
  }

  setSchema(schema: FormSchema): void {
    this.schema = schema;
    this.controlIndex = this.buildControlIndex();

    // Remove orphaned values/errors/touched for fields no longer in schema
    const validIds = new Set(this.getAllControls().map(c => c.id));
    for (const fieldId of Object.keys(this.state.values)) {
      if (!validIds.has(fieldId)) {
        delete this.state.values[fieldId];
        delete this.state.errors[fieldId];
        this.state.touched.delete(fieldId);
      }
    }

    this.initializeFields();
  }

  // ── Validation ───────────────────────────────────────────────────────────────

  validate(): FormValidationResult {
    const errors: ValidationError[] = [];
    const fieldResults: Record<string, FieldValidationResult> = {};

    for (const control of this.getAllControls()) {
      const error = this.validator.validateField(control, this.state.values[control.id], {
        schema: this.schema,
        formValues: this.state.values,
      });
      if (error) {
        errors.push(error);
        fieldResults[control.id] = { fieldId: control.id, isValid: false, error };
        this.state.errors[control.id] = error.message;
      } else {
        fieldResults[control.id] = { fieldId: control.id, isValid: true };
        delete this.state.errors[control.id];
      }
    }

    this.emitter.emit('validate', {});

    const totalFields = this.getAllControls().length;
    return {
      isValid: errors.length === 0,
      errors,
      fieldResults,
      summary: {
        totalFields,
        validFields: totalFields - errors.length,
        invalidFields: errors.length,
        errorCount: errors.length,
      },
    };
  }

  // ── Reset ────────────────────────────────────────────────────────────────────

  reset(initialValues?: Record<string, any>): void {
    this.state.reset(initialValues);
    this.bindingResolver.clearCache();
    if (!initialValues) {
      this.initializeFields();
    }
    this.emitter.emit('reset', {});
  }

  // ── Events ───────────────────────────────────────────────────────────────────

  on(eventType: FormEngineEventType, listener: (event: FormEngineEvent) => void): () => void {
    return this.emitter.on(eventType, listener);
  }

  // ── Snapshot / restore ───────────────────────────────────────────────────────

  getState() {
    return {
      values: this.getValues(),
      errors: this.getErrors(),
      touched: Array.from(this.state.touched),
      dirty: this.state.dirty,
      isValid: this.isValid(),
      isSubmitting: this.state.isSubmitting,
    };
  }

  setState(state: ReturnType<typeof this.getState>): void {
    this.state.restoreSnapshot({
      values: state.values,
      errors: state.errors,
      touched: state.touched,
      dirty: state.dirty,
      isSubmitting: state.isSubmitting,
    });
  }

  /**
   * Render is not supported in React hook mode
   * Use FormField component to render individual fields
   * This method is provided for backward compatibility
   * @throws Error indicating this is a React-based form
   */
  render(): never {
    throw new Error(
      '[FormEngine] render() is not supported. ' +
      'This FormEngine is designed for React hooks (useAtlasForm hook). ' +
      'Use the <FormField> component to render individual form fields. ' +
      'See documentation at: https://docs.atlas-forms.dev/form-engine/react-hook'
    );
  }
}
